/**
 * usePanel — Custom hook for managing panel state
 * Handles resize, open/close, overlay behavior, and pin state
 */

import { useState, useRef, useCallback, useEffect } from 'react';

interface UsePanelOptions {
  minWidth?: number;
  maxWidth?: number;
  defaultWidth?: number;
}

interface UsePanelReturn {
  isOpen: boolean;
  isPinned: boolean;
  width: number;
  isResizing: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  pin: () => void;
  unpin: () => void;
  startResize: (e: React.MouseEvent) => void;
  panelRef: React.RefObject<HTMLDivElement | null>;
}

export const usePanel = (options: UsePanelOptions = {}): UsePanelReturn => {
  const {
    minWidth = 280,
    maxWidth = 600,
    defaultWidth = 400,
  } = options;

  const [isOpen, setIsOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [width, setWidth] = useState(defaultWidth);
  const [isResizing, setIsResizing] = useState(false);

  const panelRef = useRef<HTMLDivElement | null>(null);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const pin = useCallback(() => {
    setIsPinned(true);
  }, []);

  const unpin = useCallback(() => {
    setIsPinned(false);
  }, []);

  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);

    const startX = e.clientX;
    const startWidth = width;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidth + deltaX));
      setWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [width, minWidth, maxWidth]);

  // Close panel when clicking outside (if not pinned)
  useEffect(() => {
    if (!isOpen || isPinned) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        close();
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen, isPinned, close]);

  return {
    isOpen,
    isPinned,
    width,
    isResizing,
    open,
    close,
    toggle,
    pin,
    unpin,
    startResize,
    panelRef,
  };
};
